import type { V1Event } from '@/generated/hooks/rain/models';

import type { IDeletedEventRange } from './rollback';

/**
 * Incremental generation-state model for a Rain thread.
 *
 * The conversation lock is held when the agent loop is producing output OR
 * when a compaction is in flight. It's released when the loop is parked
 * waiting on a user reply (interrupt, tool approval) or on a server sleep.
 *
 * Rather than walking the whole event log on every render (rain's UI approach in
 * [rain/ui/apps/web/src/modules/thread/api.ts isGenerationInProgress]), this
 * reducer maintains a small derived state that updates in O(1) per event. The
 * event itself is read once for its relevant fields and then dropped — nothing
 * is retained beyond the small Sets of pending domain IDs (interrupt_id,
 * tool_call_id, sleep_id, compact_id) needed to pair adds with removes.
 *
 * `isGenerationInProgress` is the reference for which states count as working;
 * this file accumulates the same classification instead of scanning for it.
 */
export interface GenerationState {
  /**
   * True between a user input and the agent settling. Re-activated by any
   * agent-activity event seen after a `generation_complete` — covers
   * multi-turn tool loops (where the server emits `generation_complete`
   * between turns), remote-tool result returns, and reconnects mid-stream.
   */
  hasActiveTurn: boolean;
  /**
   * Domain IDs of currently-pending pause reasons. Sets (not counters) so
   * replayed or duplicated events stay idempotent — adding a present id
   * or deleting a missing id is a no-op rather than a drift. These are NOT
   * SSE event ids.
   */
  pendingInterrupts: ReadonlySet<string>;
  pendingApprovals: ReadonlySet<string>;
  unmatchedSleeps: ReadonlySet<string>;
  pendingCompactions: ReadonlySet<string>;
  /**
   * Root-level tool calls dispatched and not yet resolved, by `tool_call_start`
   * event id so a rollback can drop the ones inside its deleted range. Local
   * calls mean the agent is working; remote ones mean the loop released the
   * lock and is waiting on this client.
   */
  activeLocalToolCalls: ReadonlyMap<string, number>;
  pendingRemoteToolCalls: ReadonlyMap<string, number>;
  /**
   * Whether a completion has already been held back since the last sign of
   * activity. A second one means the loop settled without resolving what it
   * dispatched, so the turn ends rather than waiting on a result forever.
   */
  hasHeldCompletion: boolean;
}

export type GenerationAction =
  /**
   * Apply one V1Event from backlog replay or SSE. `isRoot` distinguishes the
   * root loop from sub-agent task streams; turn-settling events are honored
   * only at root. Defaults to root when omitted.
   */
  | { type: 'event'; event: V1Event; isRoot?: boolean }
  /**
   * Optimistically mark the turn active from a mutation path
   * (sendMessageToThread / submitInterruptReply / submitToolApproval) so the
   * Stop button stays visible during the window between the API resolving
   * and the server's `input_text` / `interrupt_answer` / `tool_approval_*`
   * echo landing through the SSE stream.
   */
  | { type: 'start-turn' }
  /**
   * A turn was rolled back server-side (`events_deleted`). The pause ids
   * listed here belonged to the deleted range, so their resolving events
   * (interrupt_answer, tool_approval_granted, sleep_completed,
   * compact_complete, tool_result) will never arrive — without dropping them
   * the pause would never clear and `isGenerating` would be wedged for the
   * rest of the thread. The turn itself stays active: a retry is coming.
   */
  | {
      type: 'rollback';
      interruptIds: readonly string[];
      approvalIds: readonly string[];
      sleepIds: readonly string[];
      compactionIds: readonly string[];
      /** Tool calls dispatched inside it can never resolve, so they're dropped. */
      deletedEventRange?: IDeletedEventRange;
    }
  /**
   * The USER stopped the turn and the server acknowledged the request.
   *
   * The reducer otherwise settles a turn only on a server `generation_complete`.
   * When a stop lands, that event may never arrive — the loop it would have
   * ended is gone — so without this the turn stays active forever and
   * `isGenerating` wedges the composer shut: the operator stops the agent and
   * can then never say anything else.
   *
   * Optimistic, but only after `stop` has resolved, so the server has already
   * agreed. Idempotent with a late `generation_complete`, and a resumed agent
   * re-activates the turn through the ordinary activity path.
   */
  | { type: 'settle-turn' }
  /** Wipe state — used on stream error / disconnect. */
  | { type: 'reset' };

export const INITIAL_GENERATION_STATE: GenerationState = {
  hasActiveTurn: false,
  pendingInterrupts: new Set(),
  pendingApprovals: new Set(),
  unmatchedSleeps: new Set(),
  pendingCompactions: new Set(),
  activeLocalToolCalls: new Map(),
  pendingRemoteToolCalls: new Map(),
  hasHeldCompletion: false,
};

/** Whether a dispatched local call is executing rather than parked on approval. */
function hasExecutingLocalToolCall(state: GenerationState): boolean {
  if (state.activeLocalToolCalls.size === 0) return false;
  if (state.pendingApprovals.size === 0) return true;
  for (const id of state.activeLocalToolCalls.keys()) {
    if (!state.pendingApprovals.has(id)) return true;
  }
  return false;
}

/**
 * Derives the consumer-facing "is the agent currently working?" boolean.
 *
 * Compactions block the conversation regardless of turn state (a user can
 * trigger a compaction while idle, and the agent loop holds the lock the
 * entire time). Otherwise the answer is "yes if a turn is active AND either
 * a tool is executing OR no pause reason is outstanding".
 */
export function selectIsGenerating(state: GenerationState): boolean {
  if (state.pendingCompactions.size > 0) return true;
  if (!state.hasActiveTurn) return false;
  /*
   * Interrupts and sleeps first: the tool that raises one is itself a call in
   * flight, so letting a call outrank them would pin the busy state for the
   * whole pause and shut the composer the operator has to answer in.
   */
  if (state.pendingInterrupts.size > 0 || state.unmatchedSleeps.size > 0) {
    return false;
  }
  // An executing call then outranks approvals: the server holds the lock while
  // a dispatched sibling runs. Same order as rain's `isGenerationInProgress`.
  if (hasExecutingLocalToolCall(state)) return true;
  return (
    state.pendingApprovals.size === 0 && state.pendingRemoteToolCalls.size === 0
  );
}

/**
 * Returns a new Set with `id` added, or the original set if `id` was already
 * present (preserves reference equality so React can bail out of re-renders).
 */
function setWithAdd(set: ReadonlySet<string>, id: string): ReadonlySet<string> {
  if (set.has(id)) return set;
  const next = new Set(set);
  next.add(id);
  return next;
}

/**
 * Returns a new Set with `id` removed, or the original set if `id` was not
 * present (preserves reference equality).
 */
function setWithDelete(
  set: ReadonlySet<string>,
  id: string,
): ReadonlySet<string> {
  if (!set.has(id)) return set;
  const next = new Set(set);
  next.delete(id);
  return next;
}

/**
 * Returns a new Set without any of `ids`, or the original set if none were
 * present (preserves reference equality).
 */
function setWithDeleteAll(
  set: ReadonlySet<string>,
  ids: readonly string[],
): ReadonlySet<string> {
  if (!ids.some(id => set.has(id))) return set;
  const next = new Set(set);
  for (const id of ids) next.delete(id);
  return next;
}

/** Records `id`, preserving reference equality when the entry is unchanged. */
function mapWithSet(
  map: ReadonlyMap<string, number>,
  id: string,
  startEventId: number,
): ReadonlyMap<string, number> {
  if (map.get(id) === startEventId) return map;
  const next = new Map(map);
  next.set(id, startEventId);
  return next;
}

/** Drops `id`, preserving reference equality when it was absent. */
function mapWithDelete(
  map: ReadonlyMap<string, number>,
  id: string,
): ReadonlyMap<string, number> {
  if (!map.has(id)) return map;
  const next = new Map(map);
  next.delete(id);
  return next;
}

/** Drops the entries whose start event falls inside the deleted range. */
function mapWithoutRange(
  map: ReadonlyMap<string, number>,
  range: IDeletedEventRange,
): ReadonlyMap<string, number> {
  const doomed: string[] = [];
  for (const [id, startEventId] of map) {
    if (startEventId >= range.from && startEventId <= range.to) doomed.push(id);
  }
  if (doomed.length === 0) return map;
  const next = new Map(map);
  for (const id of doomed) next.delete(id);
  return next;
}

/**
 * Ends the turn and forgets everything it had in flight, so a call that will
 * never resolve cannot follow the thread into the next turn.
 */
function settleTurn(state: GenerationState): GenerationState {
  if (
    !state.hasActiveTurn &&
    !state.hasHeldCompletion &&
    state.activeLocalToolCalls.size === 0 &&
    state.pendingRemoteToolCalls.size === 0
  ) {
    return state;
  }
  return {
    ...state,
    hasActiveTurn: false,
    hasHeldCompletion: false,
    activeLocalToolCalls:
      state.activeLocalToolCalls.size === 0
        ? state.activeLocalToolCalls
        : new Map(),
    pendingRemoteToolCalls:
      state.pendingRemoteToolCalls.size === 0
        ? state.pendingRemoteToolCalls
        : new Map(),
  };
}

/**
 * Re-activates the turn if it was previously settled. Used by every event
 * that proves the agent loop is still doing work — output streaming, tool
 * activity, pause-resolution echoes. Idempotent if the turn is already
 * active (returns the same reference).
 */
function reactivate(state: GenerationState): GenerationState {
  if (state.hasActiveTurn && !state.hasHeldCompletion) return state;
  return { ...state, hasActiveTurn: true, hasHeldCompletion: false };
}

export function generationReducer(
  state: GenerationState,
  action: GenerationAction,
): GenerationState {
  if (action.type === 'reset') return INITIAL_GENERATION_STATE;
  if (action.type === 'settle-turn') {
    /*
     * Pause reasons go too. A stop abandons the loop that was waiting on them,
     * so their resolving events will never arrive, and `selectIsGenerating`
     * reports a pending compaction as generating regardless of turn state.
     */
    return !state.hasActiveTurn &&
      state.pendingInterrupts.size === 0 &&
      state.pendingApprovals.size === 0 &&
      state.unmatchedSleeps.size === 0 &&
      state.pendingCompactions.size === 0
      ? state
      : INITIAL_GENERATION_STATE;
  }
  if (action.type === 'start-turn') {
    return state.hasActiveTurn ? state : { ...state, hasActiveTurn: true };
  }
  if (action.type === 'rollback') {
    const pendingInterrupts = setWithDeleteAll(
      state.pendingInterrupts,
      action.interruptIds,
    );
    const pendingApprovals = setWithDeleteAll(
      state.pendingApprovals,
      action.approvalIds,
    );
    const unmatchedSleeps = setWithDeleteAll(
      state.unmatchedSleeps,
      action.sleepIds,
    );
    const pendingCompactions = setWithDeleteAll(
      state.pendingCompactions,
      action.compactionIds,
    );
    const { deletedEventRange } = action;
    const activeLocalToolCalls = deletedEventRange
      ? mapWithoutRange(state.activeLocalToolCalls, deletedEventRange)
      : state.activeLocalToolCalls;
    const pendingRemoteToolCalls = deletedEventRange
      ? mapWithoutRange(state.pendingRemoteToolCalls, deletedEventRange)
      : state.pendingRemoteToolCalls;
    if (
      pendingInterrupts === state.pendingInterrupts &&
      pendingApprovals === state.pendingApprovals &&
      unmatchedSleeps === state.unmatchedSleeps &&
      pendingCompactions === state.pendingCompactions &&
      activeLocalToolCalls === state.activeLocalToolCalls &&
      pendingRemoteToolCalls === state.pendingRemoteToolCalls
    ) {
      return state;
    }
    return {
      ...state,
      pendingInterrupts,
      pendingApprovals,
      unmatchedSleeps,
      pendingCompactions,
      activeLocalToolCalls,
      pendingRemoteToolCalls,
    };
  }

  const { event } = action;
  const isRoot = action.isRoot !== false;

  // Turn-starting input events: any user-originated content begins a turn.
  // input_text usually accompanies input_image/input_document, but a user can
  // send attachments only — guard each one independently.
  if (event.input_text || event.input_image || event.input_document) {
    return state.hasActiveTurn ? state : { ...state, hasActiveTurn: true };
  }

  // Pause-add events: agent has parked waiting on user/server response.
  if (event.interrupt?.interrupt_id) {
    const next = setWithAdd(
      state.pendingInterrupts,
      event.interrupt.interrupt_id,
    );
    return next === state.pendingInterrupts
      ? state
      : { ...state, pendingInterrupts: next };
  }
  if (event.tool_approval_requested?.tool_call_id) {
    const next = setWithAdd(
      state.pendingApprovals,
      event.tool_approval_requested.tool_call_id,
    );
    return next === state.pendingApprovals
      ? state
      : { ...state, pendingApprovals: next };
  }
  if (event.sleep_requested?.sleep_id) {
    const next = setWithAdd(
      state.unmatchedSleeps,
      event.sleep_requested.sleep_id,
    );
    return next === state.unmatchedSleeps
      ? state
      : { ...state, unmatchedSleeps: next };
  }
  if (event.compact_start?.compact_id) {
    const next = setWithAdd(
      state.pendingCompactions,
      event.compact_start.compact_id,
    );
    return next === state.pendingCompactions
      ? state
      : { ...state, pendingCompactions: next };
  }

  // Pause-resolve events: pause cleared AND the agent loop is resuming, so
  // the resolution itself counts as activity (re-activates the turn — fixes
  // the case where generation_complete fired between the pause-add and the
  // resolution, leaving hasActiveTurn false).
  if (event.interrupt_answer?.interrupt_id) {
    const nextSet = setWithDelete(
      state.pendingInterrupts,
      event.interrupt_answer.interrupt_id,
    );
    const withDelete =
      nextSet === state.pendingInterrupts
        ? state
        : { ...state, pendingInterrupts: nextSet };
    return reactivate(withDelete);
  }
  if (event.tool_approval_granted?.tool_call_id) {
    const nextSet = setWithDelete(
      state.pendingApprovals,
      event.tool_approval_granted.tool_call_id,
    );
    const withDelete =
      nextSet === state.pendingApprovals
        ? state
        : { ...state, pendingApprovals: nextSet };
    return reactivate(withDelete);
  }
  if (event.tool_approval_denied?.tool_call_id) {
    // The call will never run, so it must stop counting as work in flight.
    const id = event.tool_approval_denied.tool_call_id;
    return reactivate({
      ...state,
      pendingApprovals: setWithDelete(state.pendingApprovals, id),
      activeLocalToolCalls: mapWithDelete(state.activeLocalToolCalls, id),
      pendingRemoteToolCalls: mapWithDelete(state.pendingRemoteToolCalls, id),
    });
  }
  if (event.sleep_completed?.sleep_id) {
    const nextSet = setWithDelete(
      state.unmatchedSleeps,
      event.sleep_completed.sleep_id,
    );
    const withDelete =
      nextSet === state.unmatchedSleeps
        ? state
        : { ...state, unmatchedSleeps: nextSet };
    return reactivate(withDelete);
  }
  // Terminal for its `sleep_requested`, so the thread is not left suspended.
  if (event.sleep_failed?.sleep_id) {
    const nextSet = setWithDelete(
      state.unmatchedSleeps,
      event.sleep_failed.sleep_id,
    );
    const withDelete =
      nextSet === state.unmatchedSleeps
        ? state
        : { ...state, unmatchedSleeps: nextSet };
    return reactivate(withDelete);
  }

  // Compaction completion: just clear the id. Compactions don't imply a
  // surrounding generation turn, so no re-activation.
  if (event.compact_complete?.compact_id) {
    const nextSet = setWithDelete(
      state.pendingCompactions,
      event.compact_complete.compact_id,
    );
    return nextSet === state.pendingCompactions
      ? state
      : { ...state, pendingCompactions: nextSet };
  }
  if (event.compact_failed?.compact_id) {
    const nextSet = setWithDelete(
      state.pendingCompactions,
      event.compact_failed.compact_id,
    );
    return nextSet === state.pendingCompactions
      ? state
      : { ...state, pendingCompactions: nextSet };
  }

  // Root only: a sub-agent's calls must not hold the root turn open.
  if (event.tool_call_start?.id && isRoot) {
    const id = event.tool_call_start.id;
    // -1 for an unusable event id: no range covers it, so it is never dropped.
    const startEventId = Number(event.id);
    const placed = Number.isFinite(startEventId) ? startEventId : -1;
    if (event.tool_call_start.is_remote === true) {
      const next = mapWithSet(state.pendingRemoteToolCalls, id, placed);
      return reactivate(
        next === state.pendingRemoteToolCalls
          ? state
          : { ...state, pendingRemoteToolCalls: next },
      );
    }
    const next = mapWithSet(state.activeLocalToolCalls, id, placed);
    return reactivate(
      next === state.activeLocalToolCalls
        ? state
        : { ...state, activeLocalToolCalls: next },
    );
  }
  // Deliberately not gated on `isRoot`: only root calls are tracked, ids are
  // unique, and a result routed under a task scope must still clear its call.
  if (event.tool_result?.tool_call_id) {
    const id = event.tool_result.tool_call_id;
    const activeLocalToolCalls = mapWithDelete(state.activeLocalToolCalls, id);
    const pendingRemoteToolCalls = mapWithDelete(
      state.pendingRemoteToolCalls,
      id,
    );
    return reactivate(
      activeLocalToolCalls === state.activeLocalToolCalls &&
        pendingRemoteToolCalls === state.pendingRemoteToolCalls
        ? state
        : { ...state, activeLocalToolCalls, pendingRemoteToolCalls },
    );
  }

  // Terminal events: settle the turn (root-only — a sub-agent completing must
  // not clear the root turn). A retryable failure is auto-retry parking, not
  // terminal — leave the turn active so Stop keeps showing.
  if (event.generation_complete) {
    if (!isRoot) return state;
    /*
     * Not terminal while a dispatched tool is still executing: this arrives at
     * the end of the model's stream, before those tools have run. Held once
     * only — a second completion with no activity since means the loop settled
     * without resolving the call, and nothing else would ever end the turn.
     */
    if (hasExecutingLocalToolCall(state) && !state.hasHeldCompletion) {
      return { ...state, hasHeldCompletion: true };
    }
    return settleTurn(state);
  }
  if (event.generation_failed) {
    if (!isRoot || event.generation_failed.retryable === true) return state;
    // No result is coming for anything still in flight, so drop it.
    return settleTurn(state);
  }

  // Agent-activity events: any of these prove the loop is alive. Critically,
  // they re-activate the turn after a `generation_complete` — that's how a
  // multi-turn tool loop (or a remote-tool result return) keeps the Stop
  // button visible across model turns.
  if (
    event.output_text ||
    event.reasoning_text ||
    event.reasoning_summary ||
    event.tool_call_start ||
    event.tool_call_argument ||
    event.tool_result
  ) {
    return reactivate(state);
  }

  // No-op events: task lifecycle, follow-ups, hidden system prompts. These
  // don't affect root generation state. Return the same reference so React
  // skips the re-render.
  return state;
}
